import type {DragEvent} from 'react';
import {TaskCard} from './TaskCard.js';
import {QuickAddTask} from './QuickAddTask.js';
import type {Section, Task} from '../hooks/useTasks.js';

const workflowDotClass: Record<Section['workflowState'], string> = {todo: 'is-todo', in_progress: 'is-in-progress', done: 'is-done'};

export function SectionColumn({column, columns, tasks, onToggle, onOpen, onAdd, onMove, draggedTaskId, onDragStart, onDragEnd}: {
  column: {id: string; name: string; isDefault?: boolean; workflowState?: Section['workflowState']};
  columns: Array<{id: string | null; name: string}>;
  tasks: Task[];
  onToggle: (task: Task) => void;
  onOpen: (taskId: string) => void;
  onAdd: (title: string) => Promise<unknown>;
  onMove: (taskId: string, sectionId: string | null, beforeTaskId?: string | null, afterTaskId?: string | null) => void;
  draggedTaskId: string | null;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
}) {
  function dropOnColumn(event: DragEvent) {
    event.preventDefault();
    if (!draggedTaskId) return;
    const last = tasks[tasks.length - 1];
    onMove(draggedTaskId, column.id, last ? last.id : undefined, undefined);
  }
  function dropOnCard(event: DragEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    if (!draggedTaskId) return;
    const before = index > 0 ? tasks[index - 1] : undefined;
    const after = tasks[index];
    if (draggedTaskId === after?.id) return;
    onMove(draggedTaskId, column.id, before?.id, after?.id);
  }

  return (
    <section className={`board-column${draggedTaskId ? ' is-drop-target' : ''}`} onDragOver={event => event.preventDefault()} onDrop={dropOnColumn}>
      <header className="board-column__header">
        {column.workflowState && <i className={`task-section__dot ${workflowDotClass[column.workflowState]}`} aria-hidden="true"/>}
        <h2>{column.name}</h2>
        <span>{tasks.length}</span>
      </header>
      <div className="board-column__cards">
        {tasks.map((task, index) => (
          <TaskCard
            key={task.id}
            task={task}
            columns={columns}
            onToggle={() => onToggle(task)}
            onOpen={() => onOpen(task.id)}
            onMoveTo={sectionId => onMove(task.id, sectionId)}
            onDragStart={() => onDragStart(task.id)}
            onDragEnd={onDragEnd}
            onCardDragOver={event => event.preventDefault()}
            onCardDrop={event => dropOnCard(event, index)}
          />
        ))}
      </div>
      <QuickAddTask onAdd={onAdd} placeholder="Add task…"/>
    </section>
  );
}
